import { STORAGE_KEYS } from '@/constants/storageKeys'
import type { BeautySettings, BeautySettingsRequest, BeautySettingsResponse } from '@/types/beauty'
import { apiClient } from './api'

export const DEFAULT_BEAUTY_SETTINGS: BeautySettings = {
  enabled: false,
  lightening_level: 0.35,
  smoothness_level: 0.45,
  redness_level: 0.2,
  settings_version: 1,
  local_updated_at: Date.now(),
}

const BEAUTY_FIELDS = ['enabled', 'lightening_level', 'smoothness_level', 'redness_level'] as const

export const beautySettingsApi = {
  async getBeautySettings() {
    const response = await apiClient.get<BeautySettingsResponse>('/beauty-settings')
    return response.data
  },
  async updateBeautySettings(settings: BeautySettings) {
    const response = await apiClient.put<BeautySettingsResponse>('/beauty-settings', toBeautyRequest(settings))
    return response.data
  },
}

export function readLocalBeautySettings(): BeautySettings {
  if (typeof window === 'undefined') {
    return withFreshDefault()
  }
  const raw = window.localStorage.getItem(STORAGE_KEYS.beautySettings)
  if (!raw) {
    return withFreshDefault()
  }
  try {
    const parsed = JSON.parse(raw) as Partial<BeautySettings>
    if (!isValidBeautySettings(parsed)) {
      throw new Error('invalid beauty settings')
    }
    return {
      enabled: parsed.enabled,
      lightening_level: parsed.lightening_level,
      smoothness_level: parsed.smoothness_level,
      redness_level: parsed.redness_level,
      settings_version: 1,
      local_updated_at: parsed.local_updated_at,
      synced_at: typeof parsed.synced_at === 'number' ? parsed.synced_at : undefined,
    }
  } catch {
    window.localStorage.removeItem(STORAGE_KEYS.beautySettings)
    return withFreshDefault()
  }
}

export function writeLocalBeautySettings(settings: BeautySettings) {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(STORAGE_KEYS.beautySettings, JSON.stringify(settings))
}

export function fromRemoteBeautySettings(remote: BeautySettingsResponse, now = Date.now()): BeautySettings {
  const settings = {
    enabled: remote.enabled,
    lightening_level: remote.lightening_level,
    smoothness_level: remote.smoothness_level,
    redness_level: remote.redness_level,
    settings_version: 1,
    local_updated_at: now,
    synced_at: now,
  } satisfies BeautySettings
  if (!isValidBeautySettings(settings)) {
    return withFreshDefault(now)
  }
  return settings
}

export function toBeautyRequest(settings: BeautySettings): BeautySettingsRequest {
  return {
    enabled: settings.enabled,
    lightening_level: settings.lightening_level,
    smoothness_level: settings.smoothness_level,
    redness_level: settings.redness_level,
    settings_version: 1,
  }
}

export function beautyBusinessFieldsEqual(left: BeautySettings, right: BeautySettings) {
  return BEAUTY_FIELDS.every((field) => left[field] === right[field])
}

export function patchBeautySettings(settings: BeautySettings, patch: Partial<BeautySettings>): BeautySettings {
  return {
    ...settings,
    ...patch,
    settings_version: 1,
    local_updated_at: Date.now(),
    synced_at: undefined,
  }
}

function withFreshDefault(now = Date.now()): BeautySettings {
  return {
    ...DEFAULT_BEAUTY_SETTINGS,
    local_updated_at: now,
    synced_at: undefined,
  }
}

function isValidBeautySettings(value: Partial<BeautySettings>): value is BeautySettings {
  return value.settings_version === 1 &&
    typeof value.enabled === 'boolean' &&
    isBeautyLevel(value.lightening_level) &&
    isBeautyLevel(value.smoothness_level) &&
    isBeautyLevel(value.redness_level) &&
    typeof value.local_updated_at === 'number'
}

function isBeautyLevel(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
}
